// frontend/src/components/CartEnquiryForm/index.tsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, Spinner, Image } from "react-bootstrap";
import styles from "./CartEnquiryModal.module.css";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { createEnquiry } from "../../service/enquiry";

interface CartEnquiryModalProps {
  show: boolean;
  onHide: () => void;
}

interface FormState {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  hearAboutUs: string;
  pincode: string;
  address: string;
}

const validateEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const validatePhone = (phone: string) =>
  /^\+?\d{7,15}$/.test(phone.replace(/\s+/g, ""));

const validatePincode = (pincode: string) =>
  pincode === "" || /^[0-9]{4,8}$/.test(pincode.trim());

const CartEnquiryModal: React.FC<CartEnquiryModalProps> = ({
  show,
  onHide,
}) => {
  const { user } = useAuth();
  const { cart, clearCart } = useCart();
  const [formData, setFormData] = useState<FormState>({
    name: user?.name || "",
    email: user?.emailAddress || "",
    phone: user?.contactNumber || "",
    company: "",
    message: "",
    hearAboutUs: "",
    pincode: "",
    address: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitId, setSubmitId] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    // keep user prefills updated when modal opens
    if (show) {
      setFormData((prev) => ({
        ...prev,
        name: user?.name || prev.name,
        email: user?.emailAddress || prev.email,
        phone: user?.contactNumber || prev.phone,
      }));
    }
    return () => {
      mountedRef.current = false;
    };
  }, [show, user]);

  const totals = useMemo(() => {
    const totalQty = cart.reduce((s, i) => s + (i.quantity || 0), 0);
    const totalPrice = cart.reduce(
      (s, i) => s + (i.ourPrice ?? i.price ?? 0) * (i.quantity || 0),
      0
    );
    return { totalQty, totalPrice };
  }, [cart]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setError("");
  };

  const runValidation = (): boolean => {
    const errs: Record<string, string> = {};
    if (!formData.name.trim()) errs.name = "Full name is required";
    if (!formData.email.trim() || !validateEmail(formData.email))
      errs.email = "Valid email is required";
    if (!formData.phone.trim() || !validatePhone(formData.phone))
      errs.phone =
        "Valid phone is required (digits only, include country code if needed)";
    if (!validatePincode(formData.pincode)) errs.pincode = "Invalid PIN code";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!runValidation()) return;

    if (!cart || cart.length === 0) {
      setError("Your cart is empty. Add items before creating an enquiry.");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSubmitSuccess(false);

    // build enquiry payload matching your schema
    const enquiryData = {
      fullName: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      company: formData.company.trim() || undefined,
      message: formData.message.trim() || undefined,
      source: formData.hearAboutUs || "website_cart_form",
      pincode: formData.pincode.trim() || undefined,
      address: formData.address.trim() || undefined,
      enquiryItems: cart.map((item: any) => ({
        productId: item._id,
        productName: item.name,
        seriesId: item.series?._id || undefined,
        seriesTitle: item.series?.title || undefined,
        color: item.color || undefined,
        price: item.price || 0,
        ourPrice: item.ourPrice || item.price || 0,
        quantity: item.quantity || 1,
        thumbnail: item.thumbnail || item.image || undefined,
      })),
      metadata: {
        totalItems: cart.length,
        totalQuantity: totals.totalQty,
        estimatedValue: totals.totalPrice,
      },
    };

    try {
      // await network call
      const res: any = await createEnquiry(enquiryData as any);
      // expecting createEnquiry returns an object with id or success flag
      if (!mountedRef.current) return;
      setSubmitSuccess(true);
      // try to get ID from response if available
      setSubmitId(res?.id || res?.enquiryId || null);
      clearCart();
    } catch (err: any) {
      console.error("Cart enquiry error:", err);
      const msg =
        (err && err.message) ||
        (typeof err === "string"
          ? err
          : "Failed to submit enquiry. Please try again.");
      setError(msg);
      setSubmitSuccess(false);
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: user?.name || "",
      email: user?.emailAddress || "",
      phone: user?.contactNumber || "",
      company: "",
      message: "",
      hearAboutUs: "",
      pincode: "",
      address: "",
    });
    setFieldErrors({});
    setError("");
    setSubmitSuccess(false);
    setSubmitId(null);
  };

  const handleClose = () => {
    // prevent closing while submitting
    if (isSubmitting) return;
    onHide();
    resetForm();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop={isSubmitting ? "static" : true}
      className={`${styles.cartEnquiryModal} glass-effect`}
      aria-labelledby="cart-enquiry-modal-title"
    >
      <Modal.Header className={styles.header} closeButton={!isSubmitting}>
        <div className={styles.titleWrap}>
          <h5 id="cart-enquiry-modal-title" className={styles.title}>
            Cart Enquiry
          </h5>
          <small className={styles.subtitle}>
            Submit one enquiry for all items in your cart — our team will reach
            out within 24 hours.
          </small>
        </div>
        <div className={styles.meta}>
          <div className={styles.metaItem}>
            <strong>{cart.length}</strong>
            <span>Products</span>
          </div>
          <div className={styles.metaItem}>
            <strong>{totals.totalQty}</strong>
            <span>Qty</span>
          </div>
          <div className={styles.metaItem}>
            <strong>₹{totals.totalPrice.toFixed(2)}</strong>
            <span>Est. Value</span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        {submitSuccess ? (
          <div className={styles.successWrap} role="status" aria-live="polite">
            <div className={styles.successIcon} aria-hidden>
              {/* check svg */}
              <svg
                viewBox="0 0 24 24"
                width="60"
                height="60"
                fill="none"
                stroke="white"
                strokeWidth="2"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="rgba(0,0,0,0.08)"
                ></circle>
                <path
                  d="M6 12.2l3 3 9-9"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div className={styles.successMessage}>
              <h3>Thank you — Enquiry Submitted!</h3>
              <p>
                We received your enquiry for{" "}
                <strong>{cart.length} items</strong>. Our sales team will
                contact you shortly.
              </p>

              {submitId && (
                <p className={styles.reference}>
                  Reference: <span>{submitId}</span>
                </p>
              )}

              <div className={styles.successActions}>
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    resetForm();
                    onHide();
                  }}
                >
                  Close
                </Button>

                <Button
                  onClick={() => {
                    // allow user to start a fresh enquiry (keeps modal open)
                    resetForm();
                  }}
                  className="ms-2"
                >
                  New Enquiry
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            {/* inline error banner */}
            {error && (
              <div
                className={styles.errorAlert}
                role="alert"
                aria-live="assertive"
              >
                <div className={styles.errorContent}>
                  <strong>Submission failed:</strong>
                  <span>{error}</span>
                </div>
                <div className={styles.errorActions}>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={() => {
                      setError("");
                    }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            )}

            {/* Cart Items Summary */}
            <section className={styles.cartSummary} aria-label="Cart summary">
              <div className={styles.cartHeader}>
                <h6>Cart Items</h6>
                <small>{cart.length} product(s)</small>
              </div>

              <ul className={styles.cartList}>
                {cart.map((item: any) => (
                  <li key={item._id} className={styles.cartItem}>
                    <div className={styles.itemLeft}>
                      {item.thumbnail ? (
                        // Image component from react-bootstrap preserves layout
                        <Image
                          src={item.path}
                          rounded
                          className={styles.itemThumb}
                          alt={item.name}
                          width={64}
                          height={64}
                        />
                      ) : (
                        <div
                          className={styles.itemThumbPlaceholder}
                          aria-hidden
                        >
                          {item.name.charAt(0)}
                        </div>
                      )}
                    </div>

                    <div className={styles.itemBody}>
                      <div className={styles.itemTitle}>{item.name}</div>
                      <div className={styles.itemMeta}>
                        <span className={styles.itemSeries}>
                          {item.series?.title || "—"}
                        </span>
                        <span>Qty: {item.quantity}</span>
                        <span>
                          ₹{(item.ourPrice ?? item.price ?? 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            {/* form fields */}
            <div className="row">
              <Form.Group className={`col-md-6 mb-3 ${styles.formGroup}`}>
                <Form.Label>
                  Full Name <span className={styles.required}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  aria-required
                  value={formData.name}
                  onChange={handleChange}
                  className={styles.input}
                  disabled={isSubmitting}
                  isInvalid={!!fieldErrors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className={`col-md-6 mb-3 ${styles.formGroup}`}>
                <Form.Label>
                  Company <small className="text-muted">(optional)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className={`col-md-6 mb-3 ${styles.formGroup}`}>
                <Form.Label>
                  Email <span className={styles.required}>*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  isInvalid={!!fieldErrors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className={`col-md-6 mb-3 ${styles.formGroup}`}>
                <Form.Label>
                  Phone <span className={styles.required}>*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  isInvalid={!!fieldErrors.phone}
                  placeholder="+91 98765 43210"
                />
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.phone}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className="row">
              <Form.Group className={`col-md-6 mb-3 ${styles.formGroup}`}>
                <Form.Label>PIN Code</Form.Label>
                <Form.Control
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  isInvalid={!!fieldErrors.pincode}
                />
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.pincode}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className={`col-md-6 mb-3 ${styles.formGroup}`}>
                <Form.Label>How did you hear about us?</Form.Label>
                <Form.Select
                  name="hearAboutUs"
                  value={formData.hearAboutUs}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="">Select</option>
                  <option value="search">Search Engine</option>
                  <option value="social">Social Media</option>
                  <option value="referral">Referral</option>
                  <option value="catalog">Catalog</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
            </div>

            <Form.Group className={`mb-3 ${styles.formGroup}`}>
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="address"
                value={formData.address}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </Form.Group>

            <Form.Group className={`mb-3 ${styles.formGroup}`}>
              <Form.Label>Additional Requirements</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="message"
                value={formData.message}
                onChange={handleChange}
                disabled={isSubmitting}
                placeholder="e.g. delivery timeline, special finish, bulk discount requests..."
              />
              <div className={styles.charHint}>
                {formData.message.length}/500
              </div>
            </Form.Group>

            <div className={styles.actions}>
              <Button
                type="submit"
                disabled={isSubmitting}
                className={`${styles.btnPrimary} w-100`}
                aria-disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting enquiry...
                  </>
                ) : (
                  `Submit Cart Enquiry • ₹${totals.totalPrice.toFixed(2)}`
                )}
              </Button>
            </div>

            <div className={styles.footerNote}>
              By submitting, you agree to be contacted by our sales team. We
              respect your privacy.
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CartEnquiryModal;
